import type { Destination, ModuleKey } from "./types"

/** Group headings, shown above each cluster of results. */
export const MODULE_LABELS: Record<ModuleKey, string> = {
  people: "People",
  hiring: "Hiring",
  requests: "Requests",
  approvals: "Approvals",
  payroll: "Payroll",
  paySchedules: "Pay Schedules",
  wallet: "Wallets",
  ewa: "Earned Wage Access",
  benefits: "Benefits",
  performance: "Performance",
  time: "Time & Attendance",
  disciplinary: "Disciplinary",
  tasks: "Tasks",
  documents: "Documents",
  surveys: "Surveys",
  assets: "Assets",
  reports: "Reports",
  settings: "Settings",
  personal: "My Profile",
}

/**
 * Permission aliases the app gates on but the Permission enum does not define.
 *
 * `can_fulfill_requisitions` is used by navigation/request.tsx:137 and :163 and
 * by SideNavigationBar.tsx:98, yet is absent from all 243 members of
 * constants/permissions.ts. Listed here so `registry.test.ts` can still reject
 * a genuine typo while recording the gap rather than hiding it. Adding the
 * member to the enum is the real fix and belongs in that file.
 */
export const ALIASES_MISSING_FROM_ENUM = [
  // navigation/request.tsx:137 and :163, SideNavigationBar.tsx:98.
  "can_fulfill_requisitions",
  // App.tsx:3000, :3011, :3022 gate the Integrations settings on this, but the
  // enum defines `can_manage_integrations` instead. If the alias really does
  // not exist, nobody but an admin can reach those pages.
  "can_manage_integration_settings",
]

/**
 * Every destination search can offer, across the four shells.
 *
 * Scope is "whatever a role can reach from its own side navigation": the admin,
 * employee and manager sidebars, plus the approvals queues. A user should be
 * able to find any page they could otherwise navigate to by hand.
 *
 * Two rules this file follows, both enforced by tests:
 *
 * - **Routes are resolved from the router, never transcribed.** The same
 *   segment appears under several parents — there are four different `exits`
 *   routes — so only the absolute path is safe. `routeDrift.test.ts` fails if
 *   a route here does not exist.
 * - **Permissions are copied from the route's own `RequireRoutePermit`.** Where
 *   a route carries no gate, the entry is marked `ungated` and search mirrors
 *   what the sidebar already shows. Search is not the place to invent an access
 *   rule the rest of the app does not enforce; where a gate is missing, the fix
 *   belongs in the router. `routeDrift.test.ts` holds the two in step.
 */
export const destinations: Destination[] = [
  {
    id: "people.employees",
    label: "Employees",
    module: "people",
    route: "/dashboard/employees",
    keywords: [
      "employee",
      "people",
      "staff",
      "team",
      "directory",
      "headcount",
      "colleague",
    ],
    permissions: [
      "can_view_employee_details",
      "can_view_personal_details",
      "can_view_compensation_details",
      "can_view_personal_requests",
      "can_view_personal_documents",
      "can_view_personal_benefits",
      "can_view_personal_assets",
      "can_view_personal_tasks",
      "can_view_disciplinary_details",
      "can_view_personal_notes",
      "can_view_personal_logs",
    ],
    shells: ["admin"],
  },
  {
    id: "people.needs-attention",
    label: "Needs Attention",
    module: "people",
    route: "/dashboard/needs-attention",
    keywords: [
      "attention",
      "incomplete",
      "missing",
      "onboarding",
      "compliance",
      "anomaly",
    ],
    permissions: ["can_create_employees"],
    shells: ["admin"],
  },
  {
    id: "people.departments",
    label: "Departments",
    module: "people",
    route: "/dashboard/departments",
    keywords: ["department", "division", "unit", "team", "structure"],
    permissions: ["can_view_departments"],
    shells: ["admin"],
  },
  {
    id: "people.managers",
    label: "Managers",
    module: "people",
    route: "/dashboard/manager",
    keywords: ["manager", "supervisor", "line manager", "reporting"],
    permissions: [],
    ungated:
      "/dashboard/manager carries no RequireRoutePermit, so search mirrors the sidebar.",
    shells: ["admin"],
  },
  {
    id: "people.custom-groups",
    label: "Custom groups",
    module: "people",
    route: "/dashboard/custom-groups",
    keywords: ["group", "cohort", "segment", "custom"],
    permissions: ["can_view_custom_groups"],
    shells: ["admin"],
  },
  {
    id: "people.offers",
    label: "Offers",
    module: "people",
    route: "/dashboard/offers",
    keywords: ["offer", "offer letter", "contract", "hire"],
    permissions: ["can_view_offers"],
    shells: ["admin"],
  },
  {
    id: "people.exits",
    label: "Exits",
    module: "people",
    route: "/dashboard/exits",
    keywords: [
      "exit",
      "offboarding",
      "leaver",
      "resignation",
      "termination",
      "attrition",
    ],
    permissions: [],
    ungated:
      "/dashboard/exits carries no RequireRoutePermit, so search mirrors the sidebar.",
    requiresModule: ["isHrisEnabled"],
    shells: ["admin"],
  },
  {
    id: "people.external-admins",
    label: "External admins",
    module: "people",
    route: "/dashboard/external-admins",
    keywords: ["external admin", "consultant", "partner", "access"],
    permissions: [],
    ungated:
      "/dashboard/external-admins carries no RequireRoutePermit, so search mirrors the sidebar.",
    shells: ["admin"],
  },
  {
    id: "people.confirmation",
    label: "Confirmation",
    module: "people",
    route: "/dashboard/confirmation",
    keywords: ["confirmation", "probation", "confirm", "permanent", "tenure"],
    permissions: [],
    ungated:
      "/dashboard/confirmation carries no RequireRoutePermit, so search mirrors the sidebar.",
    shells: ["admin"],
  },
  {
    id: "people.org-chart",
    label: "Org. Chart",
    module: "people",
    route: "/dashboard/org-chart",
    keywords: [
      "org chart",
      "hierarchy",
      "reporting",
      "structure",
      "tree",
      "organisation",
    ],
    permissions: ["can_view_org_chart"],
    shells: ["admin"],
  },
  {
    id: "people.job-titles",
    label: "Job Titles",
    module: "people",
    route: "/dashboard/job-titles",
    keywords: ["job title", "role", "designation", "position"],
    permissions: ["can_manage_hr_&_payroll_settings"],
    shells: ["admin"],
  },
  {
    id: "people.contractors",
    label: "Contractors",
    module: "people",
    route: "/dashboard/contractor",
    keywords: ["contractor", "freelancer", "vendor", "consultant"],
    permissions: [],
    ungated:
      "/dashboard/contractor carries no RequireRoutePermit, so search mirrors the sidebar.",
    shells: ["admin"],
  },
  {
    id: "hiring.overview",
    label: "Hiring overview",
    module: "hiring",
    route: "/dashboard/hiring/dashboard",
    keywords: ["hiring", "recruitment", "ats", "overview", "talent"],
    permissions: [],
    ungated:
      "/dashboard/hiring/dashboard carries no RequireRoutePermit, so search mirrors the sidebar.",
    shells: ["admin"],
  },
  {
    id: "hiring.jobs",
    label: "Jobs",
    module: "hiring",
    route: "/dashboard/hiring/jobs",
    keywords: ["job", "vacancy", "opening", "requisition", "role", "posting"],
    permissions: [],
    ungated:
      "/dashboard/hiring/jobs carries no RequireRoutePermit, so search mirrors the sidebar.",
    shells: ["admin"],
  },
  {
    id: "hiring.applicants",
    label: "Applicants",
    module: "hiring",
    route: "/dashboard/hiring/applicants",
    keywords: ["applicant", "candidate", "cv", "resume", "pipeline"],
    permissions: [],
    ungated:
      "/dashboard/hiring/applicants carries no RequireRoutePermit, so search mirrors the sidebar.",
    shells: ["admin"],
  },
  {
    id: "hiring.settings",
    label: "Hiring settings",
    module: "hiring",
    route: "/dashboard/hiring/settings",
    keywords: ["hiring settings", "recruitment settings", "ats settings"],
    permissions: [],
    ungated:
      "/dashboard/hiring/settings carries no RequireRoutePermit, so search mirrors the sidebar.",
    shells: ["admin"],
  },
  {
    id: "requests.leaves",
    label: "Leave requests",
    module: "requests",
    route: "/dashboard/requests/list/leaves",
    keywords: [
      "leave",
      "time off",
      "holiday",
      "vacation",
      "absence",
      "pto",
      "request",
    ],
    permissions: [],
    ungated:
      "/dashboard/requests/list/leaves carries no RequireRoutePermit, so search mirrors the sidebar.",
    shells: ["admin"],
  },
  {
    id: "requests.exits",
    label: "Exit requests",
    module: "requests",
    route: "/dashboard/requests/list/exits",
    keywords: [
      "exit",
      "resignation",
      "offboarding",
      "leaver",
      "termination",
      "request",
    ],
    permissions: [],
    ungated:
      "/dashboard/requests/list/exits carries no RequireRoutePermit, so search mirrors the sidebar.",
    requiresModule: ["isHrisEnabled"],
    shells: ["admin"],
  },
  {
    id: "requests.loans",
    label: "Loan requests",
    module: "requests",
    route: "/dashboard/requests/list/loans",
    keywords: ["loan", "borrow", "credit", "request"],
    permissions: [],
    ungated:
      "/dashboard/requests/list/loans carries no RequireRoutePermit, so search mirrors the sidebar.",
    shells: ["admin"],
  },
  {
    id: "requests.salary-advances",
    label: "Salary advance requests",
    module: "requests",
    route: "/dashboard/requests/list/salary-advances",
    keywords: ["salary advance", "advance", "payday", "draw", "request"],
    permissions: [],
    ungated:
      "/dashboard/requests/list/salary-advances carries no RequireRoutePermit, so search mirrors the sidebar.",
    shells: ["admin"],
  },
  {
    id: "requests.documents",
    label: "Document requests",
    module: "requests",
    route: "/dashboard/requests/list/documents",
    keywords: ["document", "letter", "reference", "certificate", "request"],
    permissions: [],
    ungated:
      "/dashboard/requests/list/documents carries no RequireRoutePermit, so search mirrors the sidebar.",
    requiresModule: ["isHrisEnabled"],
    shells: ["admin"],
  },
  {
    id: "requests.redeployments",
    label: "Redeployment requests",
    module: "requests",
    route: "/dashboard/requests/list/redeployments",
    keywords: ["redeployment", "transfer", "move", "reassign", "request"],
    permissions: [],
    ungated:
      "/dashboard/requests/list/redeployments carries no RequireRoutePermit, so search mirrors the sidebar.",
    requiresModule: ["isHrisEnabled"],
    shells: ["admin"],
  },
  {
    id: "requests.requisitions",
    label: "Requisitions",
    module: "requests",
    route: "/dashboard/requests/list/requisitions",
    keywords: ["requisition", "purchase", "spend", "procurement", "request"],
    permissions: ["can_fulfill_requisitions"],
    shells: ["admin"],
  },
  {
    id: "approvals.leaves",
    label: "Leave approvals",
    module: "approvals",
    route: "/approval/requests/leaves",
    keywords: [
      "leave",
      "approve",
      "approval",
      "review",
      "pending",
      "request",
      "time off",
    ],
    permissions: [],
    ungated:
      "/approval/requests/leaves carries no RequireRoutePermit, so search mirrors the sidebar.",
    shells: ["admin", "approvals"],
  },
  {
    id: "approvals.exits",
    label: "Exit approvals",
    module: "approvals",
    route: "/approval/requests/exits",
    keywords: [
      "exit",
      "approve",
      "approval",
      "review",
      "pending",
      "request",
      "offboarding",
    ],
    permissions: [],
    ungated:
      "/approval/requests/exits carries no RequireRoutePermit, so search mirrors the sidebar.",
    requiresModule: ["isHrisEnabled"],
    shells: ["admin", "approvals"],
  },
  {
    id: "approvals.loans",
    label: "Loan approvals",
    module: "approvals",
    route: "/approval/requests/loans",
    keywords: ["loan", "approve", "approval", "review", "pending", "request"],
    permissions: [],
    ungated:
      "/approval/requests/loans carries no RequireRoutePermit, so search mirrors the sidebar.",
    shells: ["admin", "approvals"],
  },
  {
    id: "approvals.salary-advances",
    label: "Salary advance approvals",
    module: "approvals",
    route: "/approval/requests/salary-advances",
    keywords: [
      "salary advance",
      "approve",
      "approval",
      "review",
      "pending",
      "request",
    ],
    permissions: [],
    ungated:
      "/approval/requests/salary-advances carries no RequireRoutePermit, so search mirrors the sidebar.",
    shells: ["admin", "approvals"],
  },
  {
    id: "approvals.documents",
    label: "Document approvals",
    module: "approvals",
    route: "/approval/requests/documents",
    keywords: [
      "document",
      "approve",
      "approval",
      "review",
      "pending",
      "request",
    ],
    permissions: [],
    ungated:
      "/approval/requests/documents carries no RequireRoutePermit, so search mirrors the sidebar.",
    shells: ["admin", "approvals"],
  },
  {
    id: "approvals.redeployments",
    label: "Redeployment approvals",
    module: "approvals",
    route: "/approval/requests/redeployments",
    keywords: [
      "redeployment",
      "approve",
      "approval",
      "review",
      "pending",
      "request",
      "transfer",
    ],
    permissions: [],
    ungated:
      "/approval/requests/redeployments carries no RequireRoutePermit, so search mirrors the sidebar.",
    shells: ["admin", "approvals"],
  },
  {
    id: "approvals.requisitions",
    label: "Requisition approvals",
    module: "approvals",
    route: "/approval/requests/requisitions",
    keywords: [
      "requisition",
      "approve",
      "approval",
      "review",
      "pending",
      "request",
      "purchase",
    ],
    permissions: [],
    ungated:
      "/approval/requests/requisitions carries no RequireRoutePermit, so search mirrors the sidebar.",
    shells: ["admin", "approvals"],
  },
  {
    id: "approvals.payroll-records",
    label: "Payroll approvals",
    module: "approvals",
    route: "/approval/payrolls/records",
    keywords: ["payroll", "approve", "approval", "review", "pending", "run"],
    permissions: [],
    ungated:
      "/approval/payrolls/records carries no RequireRoutePermit, so search mirrors the sidebar.",
    requiresModule: ["isPayrollEnabled"],
    shells: ["admin", "approvals"],
  },
  {
    id: "approvals.payroll-changes",
    label: "Payroll change approvals",
    module: "approvals",
    route: "/approval/payrolls/changes",
    keywords: ["payroll change", "approve", "approval", "review", "adjustment"],
    permissions: [],
    ungated:
      "/approval/payrolls/changes carries no RequireRoutePermit, so search mirrors the sidebar.",
    requiresModule: ["isPayrollEnabled"],
    shells: ["admin", "approvals"],
  },
  {
    id: "approvals.absence",
    label: "Absence approvals",
    module: "approvals",
    route: "/approval/absence",
    keywords: [
      "absence",
      "approve",
      "approval",
      "review",
      "pending",
      "attendance",
    ],
    permissions: [],
    ungated:
      "/approval/absence carries no RequireRoutePermit, so search mirrors the sidebar.",
    shells: ["admin", "approvals"],
  },
  {
    id: "approvals.suspensions",
    label: "Suspension approvals",
    module: "approvals",
    route: "/approval/disciplinary/suspensions",
    keywords: ["suspension", "approve", "approval", "review", "disciplinary"],
    permissions: [],
    ungated:
      "/approval/disciplinary/suspensions carries no RequireRoutePermit, so search mirrors the sidebar.",
    requiresModule: ["isHrisEnabled"],
    shells: ["admin", "approvals"],
  },
  {
    id: "approvals.violations",
    label: "Violation approvals",
    module: "approvals",
    route: "/approval/violations",
    keywords: [
      "violation",
      "approve",
      "approval",
      "review",
      "disciplinary",
      "misconduct",
    ],
    permissions: [],
    ungated:
      "/approval/violations carries no RequireRoutePermit, so search mirrors the sidebar.",
    requiresModule: ["isHrisEnabled"],
    shells: ["admin", "approvals"],
  },
  {
    id: "approvals.pay-schedules",
    label: "Pay schedule approvals",
    module: "approvals",
    route: "/approval/payment-schedules/custom",
    keywords: [
      "pay schedule",
      "payment schedule",
      "approve",
      "approval",
      "review",
      "custom",
    ],
    permissions: [],
    ungated:
      "/approval/payment-schedules/custom carries no RequireRoutePermit, so search mirrors the sidebar.",
    shells: ["admin", "approvals"],
  },
  {
    id: "payroll.overview",
    label: "Payroll",
    module: "payroll",
    route: "/dashboard/payroll",
    keywords: [
      "payroll",
      "pay run",
      "salary",
      "wages",
      "overview",
      "compensation",
    ],
    permissions: ["can_view_payroll_overview"],
    requiresModule: ["isPayrollEnabled"],
    shells: ["admin"],
  },
  {
    id: "payroll.changes",
    label: "Payroll changes",
    module: "payroll",
    route: "/dashboard/payroll/payrollchange",
    keywords: ["payroll change", "adjustment", "amendment", "variation"],
    permissions: ["can_view_payroll_changes"],
    requiresModule: ["isPayrollEnabled"],
    shells: ["admin"],
  },
  {
    id: "payroll.records",
    label: "Payroll records",
    module: "payroll",
    route: "/dashboard/payroll/payrollrecord",
    keywords: ["payroll record", "payslip", "run", "history"],
    permissions: ["can_view_payroll_records"],
    requiresModule: ["isPayrollEnabled"],
    shells: ["admin"],
  },
  {
    id: "payroll.skip",
    label: "Skip Employees",
    module: "payroll",
    route: "/dashboard/payroll/payrollskip",
    keywords: ["skip", "exclude", "omit", "payroll"],
    permissions: ["can_view_skipped_employees"],
    requiresModule: ["isPayrollEnabled"],
    shells: ["admin"],
  },
  {
    id: "payroll.history",
    label: "Payroll history",
    module: "payroll",
    route: "/dashboard/payroll/history",
    keywords: ["payroll history", "past", "archive", "previous"],
    permissions: ["can_view_payroll_records"],
    requiresModule: ["isPayrollEnabled"],
    shells: ["admin"],
  },
  {
    id: "payroll.config-pay-schedules",
    label: "Pay schedules (payroll configuration)",
    module: "paySchedules",
    route: "/dashboard/payroll/configuration/pay-schedules",
    keywords: [
      "pay schedule",
      "payment schedule",
      "payschedule",
      "regular",
      "off cycle",
      "off-cycle",
      "payroll configuration",
      "configuration",
      "setup",
    ],
    permissions: [],
    ungated:
      "/dashboard/payroll/configuration/pay-schedules carries no RequireRoutePermit, so search mirrors the sidebar.",
    shells: ["admin"],
  },
  {
    id: "payroll.config-paygrades",
    label: "Pay grades",
    module: "payroll",
    route: "/dashboard/payroll/configuration/paygrades",
    keywords: [
      "paygrade",
      "pay grade",
      "band",
      "salary structure",
      "configuration",
    ],
    permissions: [],
    ungated:
      "/dashboard/payroll/configuration/paygrades carries no RequireRoutePermit, so search mirrors the sidebar.",
    shells: ["admin"],
  },
  {
    id: "payroll.config-paybands",
    label: "Pay bands",
    module: "payroll",
    route: "/dashboard/payroll/configuration/paybands",
    keywords: [
      "payband",
      "pay band",
      "range",
      "salary structure",
      "configuration",
    ],
    permissions: [],
    ungated:
      "/dashboard/payroll/configuration/paybands carries no RequireRoutePermit, so search mirrors the sidebar.",
    shells: ["admin"],
  },
  {
    id: "payroll.config-allowances",
    label: "Allowances",
    module: "payroll",
    route: "/dashboard/payroll/configuration/allowances",
    keywords: ["allowance", "benefit in kind", "addition", "configuration"],
    permissions: [],
    ungated:
      "/dashboard/payroll/configuration/allowances carries no RequireRoutePermit, so search mirrors the sidebar.",
    shells: ["admin"],
  },
  {
    id: "payroll.config-tax-reliefs",
    label: "Tax reliefs",
    module: "payroll",
    route: "/dashboard/payroll/configuration/tax-reliefs",
    keywords: ["tax relief", "relief", "deduction", "configuration"],
    permissions: [],
    ungated:
      "/dashboard/payroll/configuration/tax-reliefs carries no RequireRoutePermit, so search mirrors the sidebar.",
    shells: ["admin"],
  },
  {
    id: "payroll.config-statutory",
    label: "Statutory benefits",
    module: "payroll",
    route: "/dashboard/payroll/configuration/statutory-benefits",
    keywords: ["statutory", "pension", "nhf", "nsitf", "itf", "configuration"],
    permissions: [],
    ungated:
      "/dashboard/payroll/configuration/statutory-benefits carries no RequireRoutePermit, so search mirrors the sidebar.",
    shells: ["admin"],
  },
  {
    id: "payroll.config-tax-tables",
    label: "Tax tables",
    module: "payroll",
    route: "/dashboard/payroll/configuration/tax-tables",
    keywords: ["tax table", "paye", "tax", "configuration"],
    permissions: [],
    ungated:
      "/dashboard/payroll/configuration/tax-tables carries no RequireRoutePermit, so search mirrors the sidebar.",
    shells: ["admin"],
  },
  {
    id: "schedules.regular",
    label: "Regular pay schedules",
    module: "paySchedules",
    route: "/dashboard/payment-schedules/regular",
    keywords: [
      "pay schedule",
      "payment schedule",
      "payschedule",
      "regular",
      "recurring",
      "payment",
    ],
    permissions: ["can_view_pay_schedules"],
    shells: ["admin"],
  },
  {
    id: "schedules.custom",
    label: "Custom pay schedules",
    module: "paySchedules",
    route: "/dashboard/payment-schedules/custom",
    keywords: [
      "pay schedule",
      "payment schedule",
      "payschedule",
      "custom",
      "one off",
      "ad hoc",
      "payment",
    ],
    permissions: ["can_view_pay_schedules"],
    shells: ["admin"],
  },
  {
    id: "schedules.reversals",
    label: "Payment reversals",
    module: "paySchedules",
    route: "/dashboard/payment-schedules/reversals",
    keywords: [
      "reversal",
      "refund",
      "reverse",
      "payment",
      "clawback",
      "pay schedule",
    ],
    permissions: ["can_view_pay_schedules"],
    shells: ["admin"],
  },
  {
    id: "wallet.overview",
    label: "Wallet overview",
    module: "wallet",
    route: "/dashboard/wallets/overview",
    keywords: ["wallet", "balance", "funds", "overview", "money", "account"],
    permissions: ["can_view_wallet_overview"],
    shells: ["admin"],
  },
  {
    id: "wallet.cross-border",
    label: "Cross-border wallet",
    module: "wallet",
    route: "/dashboard/wallets/cross-border/overview",
    keywords: ["cross border", "international", "foreign", "fx", "wallet"],
    permissions: ["can_view_wallet_transactions"],
    shells: ["admin"],
  },
  {
    id: "wallet.transfers",
    label: "Transfers",
    module: "wallet",
    route: "/dashboard/wallets/transfers",
    keywords: ["transfer", "send", "payout", "money", "wallet"],
    permissions: ["can_view_wallet_transactions"],
    shells: ["admin"],
  },
  {
    id: "wallet.transactions",
    label: "Transactions",
    module: "wallet",
    route: "/dashboard/wallets/statements",
    keywords: ["transaction", "statement", "history", "ledger", "wallet"],
    permissions: ["can_view_wallet_transactions"],
    shells: ["admin"],
  },
  {
    id: "wallet.integration",
    label: "Wallet integration",
    module: "wallet",
    route: "/dashboard/wallets/integration",
    keywords: ["wallet integration", "api", "connect", "integration"],
    permissions: ["can_view_wallet_transactions"],
    shells: ["admin"],
  },
  {
    id: "ewa.analytics",
    label: "Earned Wage Access analytics",
    module: "ewa",
    route: "/dashboard/ewa/analytics",
    keywords: ["ewa", "earned wage", "analytics", "advance", "on demand pay"],
    permissions: ["can_view_analytics"],
    requiresModule: ["isEwaEnabled"],
    shells: ["admin"],
  },
  {
    id: "ewa.employees",
    label: "Earned Wage Access employees",
    module: "ewa",
    route: "/dashboard/ewa/employees",
    keywords: ["ewa", "earned wage", "employee", "enrolled", "advance"],
    permissions: ["can_view_employees"],
    requiresModule: ["isEwaEnabled"],
    shells: ["admin"],
  },
  {
    id: "ewa.withdrawals",
    label: "Earned Wage Access withdrawals",
    module: "ewa",
    route: "/dashboard/ewa/withdrawals",
    keywords: ["ewa", "earned wage", "withdrawal", "advance", "drawdown"],
    permissions: ["can_view_withdrawals"],
    requiresModule: ["isEwaEnabled"],
    shells: ["admin"],
  },
  {
    id: "ewa.invoice",
    label: "Earned Wage Access payment",
    module: "ewa",
    route: "/dashboard/ewa/invoice",
    keywords: ["ewa", "earned wage", "invoice", "payment", "billing"],
    permissions: ["can_view_payment_notes"],
    requiresModule: ["isEwaEnabled"],
    shells: ["admin"],
  },
  {
    id: "ewa.policy",
    label: "Earned Wage Access policy",
    module: "ewa",
    route: "/dashboard/ewa/policy-management",
    keywords: ["ewa", "earned wage", "policy", "rule", "eligibility"],
    permissions: [],
    ungated:
      "/dashboard/ewa/policy-management carries no RequireRoutePermit, so search mirrors the sidebar.",
    requiresModule: ["isEwaEnabled"],
    shells: ["admin"],
  },
  {
    id: "benefits.admin",
    label: "Benefits",
    module: "benefits",
    route: "/dashboard/benefits",
    keywords: ["benefit", "perk", "insurance", "pension", "hmo", "health"],
    permissions: ["can_view_benefits"],
    shells: ["admin"],
  },
  {
    id: "tasks.admin",
    label: "Tasks",
    module: "tasks",
    route: "/dashboard/tasks",
    keywords: ["task", "todo", "checklist", "assignment", "action"],
    permissions: ["can_view_tasks"],
    shells: ["admin"],
  },
  {
    id: "documents.admin",
    label: "Documents",
    module: "documents",
    route: "/dashboard/documents",
    keywords: [
      "document",
      "file",
      "template",
      "offer letter",
      "upload",
      "policy",
    ],
    permissions: ["can_view_documents_and_offer_templates"],
    requiresModule: ["isHrisEnabled"],
    shells: ["admin"],
  },
  {
    id: "surveys.admin",
    label: "Surveys",
    module: "surveys",
    route: "/dashboard/survey",
    keywords: ["survey", "poll", "questionnaire", "feedback", "engagement"],
    permissions: [],
    ungated:
      "/dashboard/survey carries no RequireRoutePermit, so search mirrors the sidebar.",
    shells: ["admin"],
  },
  {
    id: "assets.admin",
    label: "Assets",
    module: "assets",
    route: "/dashboard/assets",
    keywords: ["asset", "equipment", "device", "laptop", "inventory"],
    permissions: ["can_view_assets"],
    shells: ["admin"],
  },
  {
    id: "performance.periods",
    label: "Performance periods",
    module: "performance",
    route: "/dashboard/performance/performance-period",
    keywords: ["period", "cycle", "quarter", "performance", "review cycle"],
    permissions: ["can_view_periods"],
    requiresModule: ["isPerformanceEnabled"],
    shells: ["admin"],
  },
  {
    id: "performance.objectives",
    label: "Objectives",
    module: "performance",
    route: "/dashboard/performance/performance-objectives",
    keywords: ["objective", "okr", "goal", "target", "key result"],
    permissions: ["can_view_objectives"],
    requiresModule: ["isPerformanceEnabled"],
    shells: ["admin"],
  },
  {
    id: "performance.reviews",
    label: "Reviews",
    module: "performance",
    route: "/dashboard/performance/reviews",
    keywords: [
      "review",
      "appraisal",
      "evaluation",
      "assessment",
      "performance",
    ],
    permissions: ["can_view_reviews"],
    requiresModule: ["isPerformanceEnabled"],
    shells: ["admin"],
  },
  {
    id: "performance.groups",
    label: "Performance groups",
    module: "performance",
    route: "/dashboard/performance/groups",
    keywords: ["group", "cohort", "performance"],
    permissions: ["can_view_groups"],
    requiresModule: ["isPerformanceEnabled"],
    shells: ["admin"],
  },
  {
    id: "performance.ratings",
    label: "Ratings",
    module: "performance",
    route: "/dashboard/performance/ratings",
    keywords: ["rating", "score", "weight", "scale", "performance"],
    permissions: ["can_set_rating_weights"],
    requiresModule: ["isPerformanceEnabled"],
    shells: ["admin"],
  },
  {
    id: "performance.conversations",
    label: "Conversations",
    module: "performance",
    route: "/dashboard/performance/conversations",
    keywords: ["conversation", "one on one", "1:1", "check in", "meeting"],
    permissions: ["can_view_conversations"],
    requiresModule: ["isPerformanceEnabled"],
    shells: ["admin"],
  },
  {
    id: "performance.feedback",
    label: "Feedback",
    module: "performance",
    route: "/dashboard/performance/feedback",
    keywords: ["feedback", "praise", "comment", "360"],
    permissions: ["can_view_feedback"],
    requiresModule: ["isPerformanceEnabled"],
    shells: ["admin"],
  },
  {
    id: "time.absence",
    label: "Absence",
    module: "time",
    route: "/dashboard/time-tracking/absence",
    keywords: ["absence", "absent", "attendance", "away", "time"],
    permissions: ["can_view_absences"],
    requiresModule: ["timeTrackingEnabled"],
    shells: ["admin"],
  },
  {
    id: "time.attendance",
    label: "Time & Attendance",
    module: "time",
    route: "/dashboard/time-and-attendance/overview",
    keywords: [
      "time",
      "attendance",
      "clock in",
      "shift",
      "roster",
      "timesheet",
    ],
    permissions: [],
    ungated:
      "/dashboard/time-and-attendance/overview carries no RequireRoutePermit, so search mirrors the sidebar.",
    shells: ["admin"],
  },
  {
    id: "disciplinary.suspensions",
    label: "Suspensions",
    module: "disciplinary",
    route: "/dashboard/disciplinary/suspensions",
    keywords: ["suspension", "suspend", "disciplinary", "sanction"],
    permissions: ["can_view_suspensions"],
    requiresModule: ["isHrisEnabled"],
    shells: ["admin"],
  },
  {
    id: "disciplinary.violations",
    label: "Violations",
    module: "disciplinary",
    route: "/dashboard/disciplinary/violations",
    keywords: ["violation", "misconduct", "breach", "disciplinary", "offence"],
    permissions: ["can_view_violations"],
    requiresModule: ["isHrisEnabled"],
    shells: ["admin"],
  },
  {
    id: "disciplinary.policies",
    label: "Disciplinary policies",
    module: "disciplinary",
    route: "/dashboard/disciplinary/policies",
    keywords: ["policy", "disciplinary", "violation", "rule"],
    permissions: ["can_view_violation_policies"],
    requiresModule: ["isHrisEnabled"],
    shells: ["admin"],
  },
  {
    id: "disciplinary.triggers",
    label: "Disciplinary triggers",
    module: "disciplinary",
    route: "/dashboard/disciplinary/triggers",
    keywords: ["trigger", "automation", "rule", "disciplinary"],
    permissions: ["can_manage_triggers"],
    requiresModule: ["isHrisEnabled"],
    shells: ["admin"],
  },
  {
    id: "disciplinary.violation-types",
    label: "Violation types",
    module: "disciplinary",
    route: "/dashboard/disciplinary/violation-types",
    keywords: ["violation type", "category", "disciplinary"],
    permissions: ["can_view_violation_types"],
    requiresModule: ["isHrisEnabled"],
    shells: ["admin"],
  },
  {
    id: "reports.payroll",
    label: "Payroll reports",
    module: "reports",
    route: "/dashboard/report/payroll",
    keywords: ["report", "payroll report", "export", "analytics"],
    permissions: [],
    ungated:
      "/dashboard/report/payroll carries no RequireRoutePermit, so search mirrors the sidebar.",
    requiresModule: ["isPayrollEnabled"],
    shells: ["admin"],
  },
  {
    id: "reports.employees",
    label: "Employee reports",
    module: "reports",
    route: "/dashboard/report/employees",
    keywords: ["report", "employee report", "headcount", "export", "analytics"],
    permissions: [],
    ungated:
      "/dashboard/report/employees carries no RequireRoutePermit, so search mirrors the sidebar.",
    shells: ["admin"],
  },
  {
    id: "reports.custom",
    label: "Custom reports",
    module: "reports",
    route: "/dashboard/report/custom",
    keywords: ["report", "custom report", "builder", "export"],
    permissions: [],
    ungated:
      "/dashboard/report/custom carries no RequireRoutePermit, so search mirrors the sidebar.",
    shells: ["admin"],
  },
  {
    id: "settings.admins",
    label: "Admins",
    module: "settings",
    route: "/dashboard/settings/permissions/admins",
    keywords: ["admin", "permission", "access", "settings", "user"],
    permissions: [],
    ungated:
      "/dashboard/settings/permissions/admins carries no RequireRoutePermit, so search mirrors the sidebar.",
    shells: ["admin"],
  },
  {
    id: "settings.hr-payroll",
    label: "HR & Payroll settings",
    module: "settings",
    route: "/dashboard/settings/hr-and-payroll",
    keywords: ["hr settings", "payroll settings", "configuration", "settings"],
    permissions: ["can_manage_hr_&_payroll_settings"],
    shells: ["admin"],
  },
  {
    id: "settings.billing",
    label: "Plan & Billing",
    module: "settings",
    route: "/dashboard/settings/plan-and-billing",
    keywords: [
      "billing",
      "plan",
      "subscription",
      "invoice",
      "payment",
      "settings",
    ],
    permissions: ["can_manage_plan_&_billing_information"],
    shells: ["admin"],
  },
  {
    id: "settings.customization",
    label: "Customization",
    module: "settings",
    route: "/dashboard/settings/customization",
    keywords: [
      "customization",
      "branding",
      "logo",
      "theme",
      "appearance",
      "settings",
    ],
    permissions: ["can_manage_customization_settings"],
    shells: ["admin"],
  },
  {
    id: "settings.approval-workflow",
    label: "Approval Workflows",
    module: "settings",
    route: "/dashboard/settings/approval-workflow",
    keywords: [
      "approval",
      "workflow",
      "chain",
      "approver",
      "routing",
      "settings",
    ],
    permissions: ["can_manage_approval_workflow_settings"],
    shells: ["admin"],
  },
  {
    id: "settings.integrations",
    label: "Integrations",
    module: "settings",
    route: "/dashboard/settings/integrations",
    keywords: ["integration", "api", "connect", "webhook", "settings"],
    permissions: ["can_manage_integration_settings"],
    shells: ["admin"],
  },
  {
    id: "settings.sso",
    label: "Single Sign-On",
    module: "settings",
    route: "/dashboard/settings/sso",
    keywords: ["sso", "single sign on", "saml", "login", "settings"],
    permissions: [],
    ungated:
      "/dashboard/settings/sso carries no RequireRoutePermit, so search mirrors the sidebar.",
    shells: ["admin"],
  },
  {
    id: "settings.roles",
    label: "Roles & Permissions",
    module: "settings",
    route: "/dashboard/settings/roles-permissions",
    keywords: ["role", "permission", "access", "rbac", "privilege", "settings"],
    permissions: ["can_manage_approval_workflow_settings"],
    shells: ["admin"],
  },
  {
    id: "me.people",
    label: "People",
    module: "people",
    route: "/employee/dashboard/people",
    keywords: ["people", "colleague", "directory", "team", "coworker"],
    permissions: [],
    ungated:
      "/employee/dashboard/people carries no RequireRoutePermit, so search mirrors the sidebar.",
    shells: ["employee"],
  },
  {
    id: "me.job-and-pay",
    label: "Job & Pay",
    module: "personal",
    route: "/employee/dashboard/job-and-pay",
    keywords: ["job", "pay", "salary", "compensation", "my pay", "employment"],
    permissions: [],
    ungated:
      "/employee/dashboard/job-and-pay carries no RequireRoutePermit, so search mirrors the sidebar.",
    shells: ["employee"],
  },
  {
    id: "me.personal",
    label: "Personal",
    module: "personal",
    route: "/employee/dashboard/personal",
    keywords: ["personal", "my details", "profile", "contact", "address"],
    permissions: [],
    ungated:
      "/employee/dashboard/personal carries no RequireRoutePermit, so search mirrors the sidebar.",
    shells: ["employee"],
  },
  {
    id: "me.org-chart",
    label: "Org. Chart",
    module: "people",
    route: "/employee/dashboard/org-chart",
    keywords: ["org chart", "hierarchy", "structure", "reporting"],
    permissions: [],
    ungated:
      "/employee/dashboard/org-chart carries no RequireRoutePermit, so search mirrors the sidebar.",
    shells: ["employee"],
  },
  {
    id: "me.leave",
    label: "My leave requests",
    module: "requests",
    route: "/employee/dashboard/requests/leave",
    keywords: [
      "leave",
      "time off",
      "holiday",
      "vacation",
      "pto",
      "my leave",
      "request",
    ],
    permissions: [],
    ungated:
      "/employee/dashboard/requests/leave carries no RequireRoutePermit, so search mirrors the sidebar.",
    shells: ["employee"],
  },
  {
    id: "me.loans",
    label: "My loan requests",
    module: "requests",
    route: "/employee/dashboard/requests/loans",
    keywords: ["loan", "borrow", "credit", "my loan", "request"],
    permissions: [],
    ungated:
      "/employee/dashboard/requests/loans carries no RequireRoutePermit, so search mirrors the sidebar.",
    shells: ["employee"],
  },
  {
    id: "me.salary-advances",
    label: "My salary advances",
    module: "requests",
    route: "/employee/dashboard/requests/salary-advances",
    keywords: ["salary advance", "advance", "payday", "my advance", "request"],
    permissions: [],
    ungated:
      "/employee/dashboard/requests/salary-advances carries no RequireRoutePermit, so search mirrors the sidebar.",
    shells: ["employee"],
  },
  {
    id: "me.documents-requests",
    label: "My document requests",
    module: "requests",
    route: "/employee/dashboard/requests/documents",
    keywords: [
      "document request",
      "letter",
      "reference",
      "certificate",
      "request",
    ],
    permissions: [],
    ungated:
      "/employee/dashboard/requests/documents carries no RequireRoutePermit, so search mirrors the sidebar.",
    shells: ["employee"],
  },
  {
    id: "me.exits",
    label: "My exit request",
    module: "requests",
    route: "/employee/dashboard/requests/exits",
    keywords: [
      "exit",
      "resignation",
      "resign",
      "offboarding",
      "leave company",
      "request",
    ],
    permissions: [],
    ungated:
      "/employee/dashboard/requests/exits carries no RequireRoutePermit, so search mirrors the sidebar.",
    requiresModule: ["isHrisEnabled"],
    shells: ["employee"],
  },
  {
    id: "me.redeployments",
    label: "My redeployment requests",
    module: "requests",
    route: "/employee/dashboard/requests/redeployments",
    keywords: ["redeployment", "transfer", "move", "request"],
    permissions: [],
    ungated:
      "/employee/dashboard/requests/redeployments carries no RequireRoutePermit, so search mirrors the sidebar.",
    shells: ["employee"],
  },
  {
    id: "me.requisitions",
    label: "My requisitions",
    module: "requests",
    route: "/employee/dashboard/requests/requisitions",
    keywords: ["requisition", "purchase", "spend", "request"],
    permissions: [],
    ungated:
      "/employee/dashboard/requests/requisitions carries no RequireRoutePermit, so search mirrors the sidebar.",
    shells: ["employee"],
  },
  {
    id: "me.documents",
    label: "Documents",
    module: "documents",
    route: "/employee/dashboard/document",
    keywords: ["document", "file", "my documents", "upload", "policy"],
    permissions: [],
    ungated:
      "/employee/dashboard/document carries no RequireRoutePermit, so search mirrors the sidebar.",
    shells: ["employee"],
  },
  {
    id: "me.surveys",
    label: "Surveys",
    module: "surveys",
    route: "/employee/dashboard/survey",
    keywords: ["survey", "poll", "questionnaire", "feedback"],
    permissions: [],
    ungated:
      "/employee/dashboard/survey carries no RequireRoutePermit, so search mirrors the sidebar.",
    shells: ["employee"],
  },
  {
    id: "me.benefits",
    label: "Benefits",
    module: "benefits",
    route: "/employee/dashboard/benefits",
    keywords: ["benefit", "perk", "insurance", "pension", "hmo", "health"],
    permissions: [],
    ungated:
      "/employee/dashboard/benefits carries no RequireRoutePermit, so search mirrors the sidebar.",
    shells: ["employee"],
  },
  {
    id: "me.payables",
    label: "Payables",
    module: "personal",
    route: "/employee/dashboard/payables",
    keywords: ["payable", "owed", "payment", "my payments"],
    permissions: [],
    ungated:
      "/employee/dashboard/payables carries no RequireRoutePermit, so search mirrors the sidebar.",
    shells: ["employee"],
  },
  {
    id: "me.payslips",
    label: "Payslips",
    module: "personal",
    route: "/employee/dashboard/payslip",
    keywords: ["payslip", "pay slip", "salary slip", "pay stub", "earnings"],
    permissions: [],
    ungated:
      "/employee/dashboard/payslip carries no RequireRoutePermit, so search mirrors the sidebar.",
    shells: ["employee"],
  },
  {
    id: "me.wallet",
    label: "Wallet",
    module: "wallet",
    route: "/employee/dashboard/wallet/cross-border/overview",
    keywords: ["wallet", "balance", "funds", "money", "account"],
    permissions: [],
    ungated:
      "/employee/dashboard/wallet/cross-border/overview carries no RequireRoutePermit, so search mirrors the sidebar.",
    shells: ["employee"],
  },
  {
    id: "me.wallet-transactions",
    label: "Wallet transactions",
    module: "wallet",
    route: "/employee/dashboard/wallet/cross-border/transactions",
    keywords: ["transaction", "statement", "history", "wallet"],
    permissions: [],
    ungated:
      "/employee/dashboard/wallet/cross-border/transactions carries no RequireRoutePermit, so search mirrors the sidebar.",
    shells: ["employee"],
  },
  {
    id: "me.wallet-transfers",
    label: "Wallet transfers",
    module: "wallet",
    route: "/employee/dashboard/wallet/cross-border/transfers",
    keywords: ["transfer", "send", "payout", "wallet"],
    permissions: [],
    ungated:
      "/employee/dashboard/wallet/cross-border/transfers carries no RequireRoutePermit, so search mirrors the sidebar.",
    shells: ["employee"],
  },
  {
    id: "me.ewa",
    label: "Earned Wage Access",
    module: "ewa",
    route: "/employee/dashboard/wallet/earned-wage-access/overview",
    keywords: ["ewa", "earned wage", "advance", "on demand pay", "withdraw"],
    permissions: [],
    ungated:
      "/employee/dashboard/wallet/earned-wage-access/overview carries no RequireRoutePermit, so search mirrors the sidebar.",
    requiresModule: ["isEwaEnabled"],
    shells: ["employee"],
  },
  {
    id: "me.bill-payments",
    label: "Bill Payments",
    module: "wallet",
    route: "/employee/dashboard/wallet/bill-payments",
    keywords: ["bill", "airtime", "data", "utility", "payment"],
    permissions: [],
    ungated:
      "/employee/dashboard/wallet/bill-payments carries no RequireRoutePermit, so search mirrors the sidebar.",
    shells: ["employee"],
  },
  {
    id: "me.assets",
    label: "Assets",
    module: "assets",
    route: "/employee/dashboard/assets",
    keywords: ["asset", "equipment", "device", "laptop", "my assets"],
    permissions: [],
    ungated:
      "/employee/dashboard/assets carries no RequireRoutePermit, so search mirrors the sidebar.",
    shells: ["employee"],
  },
  {
    id: "me.tasks",
    label: "Tasks",
    module: "tasks",
    route: "/employee/dashboard/tasks",
    keywords: ["task", "todo", "checklist", "my tasks", "action"],
    permissions: [],
    ungated:
      "/employee/dashboard/tasks carries no RequireRoutePermit, so search mirrors the sidebar.",
    shells: ["employee"],
  },
  {
    id: "me.time-attendance",
    label: "Time & Attendance",
    module: "time",
    route: "/employee/dashboard/time-and-attendance",
    keywords: ["time", "attendance", "clock in", "shift", "timesheet"],
    permissions: [],
    ungated:
      "/employee/dashboard/time-and-attendance carries no RequireRoutePermit, so search mirrors the sidebar.",
    shells: ["employee"],
  },
  {
    id: "me.disciplinary",
    label: "Disciplinary",
    module: "disciplinary",
    route: "/employee/dashboard/disciplinary",
    keywords: ["disciplinary", "violation", "suspension", "my record"],
    permissions: [],
    ungated:
      "/employee/dashboard/disciplinary carries no RequireRoutePermit, so search mirrors the sidebar.",
    requiresModule: ["isHrisEnabled"],
    shells: ["employee"],
  },
  {
    id: "me.notes",
    label: "Notes",
    module: "personal",
    route: "/employee/dashboard/notes",
    keywords: ["note", "comment", "record", "my notes"],
    permissions: [],
    ungated:
      "/employee/dashboard/notes carries no RequireRoutePermit, so search mirrors the sidebar.",
    shells: ["employee"],
  },
  {
    id: "me.performance",
    label: "Performance",
    module: "performance",
    route: "/employee/dashboard/performance/overview",
    keywords: ["performance", "review", "appraisal", "my performance"],
    permissions: [],
    ungated:
      "/employee/dashboard/performance/overview carries no RequireRoutePermit, so search mirrors the sidebar.",
    requiresModule: ["isPerformanceEnabled"],
    shells: ["employee"],
  },
  {
    id: "me.okrs",
    label: "My OKRs / KPIs",
    module: "performance",
    route: "/employee/dashboard/performance/okr",
    keywords: ["okr", "objective", "goal", "key result", "target"],
    permissions: [],
    ungated:
      "/employee/dashboard/performance/okr carries no RequireRoutePermit, so search mirrors the sidebar.",
    requiresModule: ["isPerformanceEnabled"],
    shells: ["employee"],
  },
  {
    id: "me.perf-reviews",
    label: "My reviews",
    module: "performance",
    route: "/employee/dashboard/performance/reviews",
    keywords: ["review", "appraisal", "evaluation", "my review"],
    permissions: [],
    ungated:
      "/employee/dashboard/performance/reviews carries no RequireRoutePermit, so search mirrors the sidebar.",
    requiresModule: ["isPerformanceEnabled"],
    shells: ["employee"],
  },
  {
    id: "mgr.direct-reports",
    label: "Direct Reports",
    module: "people",
    route: "/manager/direct-reports",
    keywords: [
      "direct report",
      "team",
      "my team",
      "reportee",
      "staff",
      "employee",
    ],
    permissions: [],
    ungated:
      "/manager/direct-reports carries no RequireRoutePermit, so search mirrors the sidebar.",
    shells: ["manager"],
  },
  {
    id: "mgr.performance",
    label: "Team performance",
    module: "performance",
    route: "/manager/performance/overview",
    keywords: ["performance", "team performance", "overview", "appraisal"],
    permissions: [],
    ungated:
      "/manager/performance/overview carries no RequireRoutePermit, so search mirrors the sidebar.",
    requiresModule: ["isPerformanceEnabled"],
    shells: ["manager"],
  },
  {
    id: "mgr.okrs",
    label: "Team OKRs / KPIs",
    module: "performance",
    route: "/manager/performance/okr",
    keywords: ["okr", "objective", "goal", "key result", "target", "team"],
    permissions: [],
    ungated:
      "/manager/performance/okr carries no RequireRoutePermit, so search mirrors the sidebar.",
    requiresModule: ["isPerformanceEnabled"],
    shells: ["manager"],
  },
  {
    id: "mgr.okr-reviews",
    label: "OKR reviews",
    module: "performance",
    route: "/manager/performance/okr/review",
    keywords: ["okr review", "review", "appraisal", "evaluation", "team"],
    permissions: [],
    ungated:
      "/manager/performance/okr/review carries no RequireRoutePermit, so search mirrors the sidebar.",
    requiresModule: ["isPerformanceEnabled"],
    shells: ["manager"],
  },
]
